// MessagesPage.jsx — 第一百零九批 D：一对一站内信（收件箱 / 发件箱 / 写信 / 撤回）。
//
// ── 它补的是哪一条 ──────────────────────────────────────────────────────────
// 系统已有三条通信面：组间线程（组对组、全员可读）、项目汇报（一对多、只读）、
// 定向密递（一对一、正文一次性）。缺的是**一对一、可回看、可撤回**的那一条——
// 此前它由需求方人肉代传。
//
// ── 这一页最要紧的一条规矩：列表面绝不出现正文或摘要（裁定 3）─────────────────
// 「点开正文即已读」把"列表不许有正文"从一条纪律升级成了**判据的前提**：他在列表里
// 读到了、而系统说未读，撤回边界与防 spam 判据同时失真。
// 三道锁，缺一不可：
//   ① 后端的行结构里就没有正文字段（列表端点想泄漏也无从泄漏）；
//   ② pages/messages.mjs 的 normalize 不留任何正文形状的字段；
//   ③ 下方那段列表由两条标记注释圈出，messages.test.mjs 把标记之间的文本当**源码**
//      读，逐条禁掉全部正文形状的字段名。前两道是数据形状，第三道管的是组件源码——
//      第一百零八批 R1 坐实过：判定还在、语义也对，只是没有人调它，而全套测试仍然全绿。
//
// ── 正文只从一条路出来，而且那一下就是已读 ──────────────────────────────────
// `api.openMessage(id)` **有副作用**：收件人调它就把这封信标成已读（对方收到回执、
// 撤回窗口永久关死）。所以它只被"用户明确点了打开"这一条路径调用——不进 useLoad 的
// 自动加载、不被铃铛的跳转顺手触发（铃铛只跳列表 + focus，见 notifTarget.mjs）。
// 收件人打开一封未读信之前必须先过一句确认（needsReadConfirm），而看自己的发件箱、
// 或重看一封已读的信不弹——一个到处弹的确认框会被训练成闭眼点确定。
//
// ── 提醒是分钟级的，文案不许承诺即时 ────────────────────────────────────────
// NotificationBell 是 60 秒轮询、没有推送；「因未读排队」的信还有 15 分钟的复查间隔。
// 所以本页所有关于"对方会收到"的话都写成会迟到的样子（i18n 侧 dm.subtitle 起头就说
// 了这件事）。
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Divider, FormControl, FormControlLabel, InputLabel, MenuItem,
  Paper, Select, Stack, Switch, TableBody, TableCell, TableHead, TableRow, Tab, Tabs, TextField,
  Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import AppSnackbar, { useNotify } from '../components/AppSnackbar.jsx';
import DataTable from '../components/DataTable.jsx';
import HelpLink from '../components/HelpLink.jsx';
import MarkdownBody from '../components/MarkdownBody.jsx';
import ResponseDeadlineBar from '../components/ResponseDeadlineBar.jsx';
import { fmtTime } from '../components/TicketDetailView.jsx';
import { useLoad } from '../hooks/useLoad.mjs';
import { useLang, useT } from '../i18n/index.mjs';
import {
  DM_BODY_MAX, DM_ERROR_KEYS, DM_PAGE_LIMIT, anchorId, anchorLabelKey, anchorRefMs, boxFromSearch,
  composePayload, composeProblem, exposureText, failReasonKey, filterAnchors, filterUnreplied,
  counterpartLabel,
  focusIdFromSearch, mayQuickReply, needsReadConfirm, normalizeAnchorFeed, normalizeMessage,
  normalizeMessages, normalizeRecipients, pickAnchorId, recipientOptionLabel, recipientProblem,
  repliedText, replyPrefill, reportedText, stateColor,
  stateLabelKey, unreadCountOf, unrepliedEmptyKey, willSendText, withdrawDescKey, withdrawFailure,
} from './messages.mjs';

// SB-190 §3.3：responseRequired 缺省 false——不勾就不送（composePayload），后端也只认真 true。
const EMPTY_FORM = { to: '', anchorId: '', body: '', bodyFormat: 'markdown', schedule: '', responseRequired: false };

// 锚点那一行的文案：列表/详情/引用块共用**这一只**（reply 用时刻，不用裸消息 id——
// anchorRefMs 头注；两处各写一遍迟早在其中一处漂掉）。
function anchorLineText(row, lang, t) {
  const key = anchorLabelKey(row.anchorKind);
  const ms = anchorRefMs(row);
  let ref = ms != null ? fmtTime(ms, lang) : (row.anchorLabel || row.anchorRef);
  // 诚实回落要**说出来**（SB-162，需求方裁②）：服务端没有该语言的译文时给的是原文，
  // 此处照工作项页的既有做法缀一句「原文 zh」——读者据此知道这是回落，不是系统坏了。
  // 复用同一个文案键而不新造措辞：同一件事在两个页面上说两种话，本身就是一处漂移。
  if (row.anchorLabelLang && row.anchorLabelLang !== lang && ms == null) {
    ref = `${ref}（${t('workItems.untranslated', { lang: row.anchorLabelLang })}）`;
  }
  return key ? t(key, { ref }) : row.anchorRef;
}

export default function MessagesPage() {
  const t = useT();
  const { lang } = useLang();
  const location = useLocation();
  const { snack, notify, clearSnack } = useNotify();

  // 铃铛给的那条链：`/messages?box=inbox&focus={id}`。box 只作**初值**——用户切了
  // 标签之后 URL 不再是权威（否则每次切换都要改地址栏，而返回键会把他弹回去）。
  const [box, setBox] = useState(() => boxFromSearch(location.search));
  const focusId = focusIdFromSearch(location.search);

  // 窗口大小走 DM_PAGE_LIMIT（messages.mjs）：空态那句话的口径要用同一个数判，
  // 手写两遍的下场是改了查询忘了文案（见 unrepliedEmptyKey 头注）。
  const list = useLoad(() => api.messages({ box, limit: DM_PAGE_LIMIT }), [box]);
  // ── 加载更多（SB-159 游标分页）──────────────────────────────────────────
  // 追加页单独存：useLoad 是一次性加载器，没有分页概念；把后续页塞回它的 data 会
  // 让"换箱子重取"与"翻下一页"变成同一个动作。换箱即清空追加页（不然 outbox 的行
  // 会跟着 inbox 一起显示——同一个数组喂两个箱子是经典的串味）。
  const [more, setMore] = useState([]);
  const [moreState, setMoreState] = useState({ loading: false, err: null });
  useEffect(() => { setMore([]); setMoreState({ loading: false, err: null }); }, [box]);
  const pageRows = normalizeMessages(list.data);
  const allRows = [...pageRows, ...more];
  // 还有没有更多：以**最后一次取回的那一页**为准（服务端说的，不是前端按行数猜的）。
  const lastCursor = more.length ? more[more.length - 1].id : (pageRows.at(-1)?.id ?? null);
  const hasMore = more.length ? moreState.hasMore === true : list.data?.hasMore === true;
  const loadMore = async () => {
    if (!lastCursor || moreState.loading) return;
    setMoreState({ loading: true, err: null });
    try {
      const d = await api.messages({ box, limit: DM_PAGE_LIMIT, before: lastCursor });
      setMore((prev) => [...prev, ...normalizeMessages(d)]);
      setMoreState({ loading: false, err: null, hasMore: d?.hasMore === true });
    } catch (e) {
      setMoreState({ loading: false, err: e, hasMore: true });
    }
  };
  // ── 第一百二十五批 「只看未回复」：**纯前端过滤**，服务端不加参数 ────────────────────
  // 收件箱最常被问的那一句是"谁还欠我回信"。判据在 filterUnreplied（服务端已经把
  // repliedAtMs 如实给了每一行，再加一个查询参数就是同一条判据的第二个实现）。
  // 开关**不进 URL**：它是一次性的看法，不是一个要被分享或后退键记住的位置（box 那一位
  // 进 URL 是因为铃铛要深链过来，这一位没有那个消费者）。
  const [unrepliedOnly, setUnrepliedOnly] = useState(false);
  const rows = filterUnreplied(allRows, unrepliedOnly);
  const unread = unreadCountOf(list.data);

  // 详情：{ row, body, bodyFormat } —— 只在用户明确点了"打开"之后才有值。
  const [detail, setDetail] = useState(null);
  const [detailErr, setDetailErr] = useState(null);
  const [confirmRow, setConfirmRow] = useState(null);   // 「打开就算已读」的那一问
  const [withdrawRow, setWithdrawRow] = useState(null);
  // 举报窄通道的那一问。它与撤回那一问是**两个** state，不是一个带 mode 的对话框：
  // 两件事的后果相反（撤回是收回，交出是永久放出去），而一个会换措辞的对话框迟早
  // 会在某次改动后把其中一句显示在另一件事上。
  const [reportRow, setReportRow] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  // SB-150 快捷回复：非空 ⇒ ComposeDialog 以预填形态打开（收件人与锚点锁定）。
  // 详情框刻意**不关**：写回复时不在写信框里复述原文（契约 §2），原文就在身后。
  const [replyRow, setReplyRow] = useState(null);
  const [busy, setBusy] = useState(false);

  const errText = (e, fallback) => t(errKey(e, { byCode: DM_ERROR_KEYS, fallback }));

  // 唯一一处调 openMessage 的地方（头注）。
  const doOpen = async (row) => {
    setConfirmRow(null);
    setBusy(true);
    setDetailErr(null);
    try {
      const r = await api.openMessage(row.id);
      setDetail({ row: { ...row, ...r, id: String(r.id ?? row.id) },
                  body: typeof r.body === 'string' ? r.body : '',
                  bodyFormat: r.bodyFormat });
      // 读一封会改变它自己的状态（也可能改变对方那条提醒），清单跟着重取一次。
      list.reload();
    } catch (e) {
      // 撤回过的信按身份给两句不同的话：收件人要知道"去问他"，发送方要知道
      // "系统没留副本"。410 走 byStatus 之前先按 direction 分岔。
      const gone = e?.status === 410;
      const key = gone
        ? (row.direction === 'out' ? 'dm.read.goneMine' : 'dm.read.gone')
        : errKey(e, { byStatus: { 404: 'dm.read.notFound' }, fallback: 'dm.error.detail' });
      setDetailErr(t(key));
      // 410 时服务端刚亲口断言了 withdrawn——快照跟着改，否则冻结的列表旧行
      // （state 还是 sent_unread）会让「回复」钮亮在一封已撤回的信上，用户写完
      // 正文换来一句「换一个他能打开的锚点」，而锁定表单根本不给换（证伪评审 S1）。
      setDetail({ row: gone ? { ...row, state: 'withdrawn' } : row,
                  body: '', bodyFormat: 'plain' });
      list.reload();
    } finally {
      setBusy(false);
    }
  };

  const onOpenRow = (row) => {
    if (needsReadConfirm(row)) { setConfirmRow(row); return; }
    doOpen(row);
  };

  const doWithdraw = async () => {
    const row = withdrawRow;
    if (!row) return;
    setBusy(true);
    try {
      await api.withdrawMessage(row.id);
      notify(t('dm.withdraw.ok'), 'success');
      setWithdrawRow(null);
      setDetail(null);
      list.reload();
    } catch (e) {
      // 三个 409 分码各说一句：已读要去当面解释、已撤回是重复点了、已失败要重发。
      // 一句笼统的"撤不了"让这三件事都做不了。时刻优先取服务端报文里的那个——
      // 我手里这一行**恰恰是过期的**（撤回失败正是因为世界在上次取数之后变了）。
      const f = withdrawFailure(e, row);
      notify(f ? t(f.key, { t: fmtTime(f.ms, lang) }) : errText(e, 'dm.withdraw.error'), 'error');
      list.reload();
    } finally {
      setBusy(false);
    }
  };

  // ── 举报窄通道：交出这一封 ────────────────────────────────────────────────
  // **只从确认框里调**（reportRow 非空才走得到这里）。理由与 takeSecretDelivery 同族：
  // 这是一个不可撤销、且把一段私下的正文放到管理面上的动作，它绝不能是一次误点。
  // 成功之后清掉详情：那一行的 mayReport 变了，而留着一个按钮还亮着的旧详情，
  // 下一次点击会拿到 409。
  const doReport = async () => {
    const row = reportRow;
    if (!row) return;
    setBusy(true);
    try {
      await api.reportMessage(row.id);
      notify(t('dm.report.ok'), 'success');
      setReportRow(null);
      setDetail(null);
      list.reload();
    } catch (e) {
      notify(errText(e, 'dm.report.error'), 'error');
      list.reload();
    } finally {
      setBusy(false);
    }
  };

  // 与选锚点那一侧同形；实现在模块级 anchorLineText（引用块也要用同一句）。
  const anchorText = (row) => anchorLineText(row, lang, t);

  const stateChip = (row) => {
    const key = stateLabelKey(row.state);
    return <Chip size="small" color={stateColor(row.state)}
                 label={key ? t(key) : row.state || '—'} />;
  };

  // 一行上的时刻取"这一态最有意义的那个"：排队看创建时刻，其余看它真正发生的时刻。
  const rowTime = (row) => row.readAtMs ?? row.withdrawnAtMs ?? row.failedAtMs
    ?? row.sentAtMs ?? row.createdAtMs;

  // ── SB-150 预填一处定形：replyPrefill 是唯一来源 ──────────────────────────
  // to = counterpartEmail（**绝不是显示名**——SB-147 病根）；锚点 = reply:<本信id>。
  // 锁定的锚点行文案用被回复那封的 sentAtMs（收件箱行恒有：未发出的信进不了收件箱），
  // 与 reply 锚点在一切读面的呈现同形。
  const prefill = replyRow ? replyPrefill(replyRow) : null;
  const prefillForm = prefill
    ? { ...EMPTY_FORM, to: prefill.to,
        anchorId: anchorId({ kind: prefill.anchorKind, ref: prefill.anchorRef }) }
    : null;
  const lockedAnchorLabel = replyRow
    ? t('dm.anchor.reply', {
        ref: replyRow.sentAtMs != null ? fmtTime(replyRow.sentAtMs, lang) : String(replyRow.id),
      })
    : '';

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
        {t('dm.title')}
        {/* SB-151：本页只挂这两枚 ❓（页头 + 撤回确认框）——A 轨（SB-150）刚落，别处不动。 */}
        <HelpLink section="messages" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('dm.subtitle')}
      </Typography>

      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 1 }}
      >
        <Tabs value={box} onChange={(e, v) => setBox(v)} sx={{ minHeight: 40 }}>
          <Tab value="inbox" label={t('dm.tab.inbox')} sx={{ minHeight: 40 }} />
          <Tab value="outbox" label={t('dm.tab.outbox')} sx={{ minHeight: 40 }} />
        </Tabs>
        {box === 'inbox' && unread != null && unread > 0 ? (
          <Chip size="small" color="info" label={t('dm.unread.badge', { n: unread })} />
        ) : null}
        {/* 第一百二十五批「已回复」：「只看未回复」。收发两侧都给——发件箱那一侧问的是"谁还没回我"，
            收件箱那一侧问的是"我还欠谁回信"，同一个开关答两个都要紧的问题。 */}
        <FormControlLabel
          sx={{ ml: 0.5 }}
          control={<Switch size="small" checked={unrepliedOnly}
                           onChange={(e) => setUnrepliedOnly(e.target.checked)} />}
          label={<Typography variant="body2">{t('dm.filter.unreplied')}</Typography>}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" onClick={() => setComposeOpen(true)}>
          {t('dm.new.title')}
        </Button>
      </Stack>

      {/* MESSAGE LIST BEGIN — 裁定 3 的守区。这一段里绝不许出现正文或由正文算出来的
          任何东西（摘要、字节数、主题）。messages.test.mjs 把这两个标记之间的文本
          逐条比对；改动这一段之前先读本文件头注。 */}
      {list.status === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : list.status === 'error' ? (
        <Box>
          <Alert severity="error" sx={{ mb: 1 }}>
            {t(errKey(list.error, { fallback: 'dm.error.list' }))}
          </Alert>
          <Button size="small" onClick={list.reload}>{t('common.retry')}</Button>
        </Box>
      ) : rows.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          {/* 空清单的两种理由必须分开说：箱子本来就空 vs. 开关把它们都滤掉了。
              说错的那一句（"没有收到过站内信"）会让人以为信丢了，而真相是"都回过了"
              ——一句确定的假话，且他手上正好有一个能推翻它的开关。
              而"都回过了"这句话本身也只在**没取满一页**时才敢说整箱（unrepliedEmptyKey）：
              这一页只有 DM_PAGE_LIMIT 行，滤的是这一页、断言的是整箱。 */}
          <Typography color="text.secondary">
            {allRows.length > 0
              ? t(unrepliedEmptyKey(allRows.length))
              : t(box === 'outbox' ? 'dm.empty.outbox' : 'dm.empty.inbox')}
          </Typography>
        </Paper>
      ) : (
        <DataTable minWidth={700}>
          <TableHead>
            <TableRow>
              <TableCell>{t('dm.col.counterpart')}</TableCell>
              <TableCell>{t('dm.col.anchor')}</TableCell>
              <TableCell>{t('dm.col.state')}</TableCell>
              <TableCell>{t('dm.col.time')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} selected={focusId === row.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {/* SB-147：并排邮箱（counterpartLabel）——人看见的身份要与
                        快捷回复实际使用的身份是同一个。 */}
                    {t(row.direction === 'out' ? 'dm.dir.out' : 'dm.dir.in',
                       { who: counterpartLabel(row) })}
                  </Typography>
                  {/* SB-148 B：系统信标记——收件人要能分辨这不是某个人手写的。 */}
                  {row.system ? (
                    <Chip size="small" color="info" variant="outlined"
                          label={t('dm.system.chip')} sx={{ mt: 0.5 }} />
                  ) : null}
                  {/* SB-190 §3.3：要求响应——收发两侧同一枚（收件人要知道头上有只钟，
                      发送方要知道对方看得见）。 */}
                  {row.responseRequired ? (
                    <Chip size="small" color="warning" variant="outlined"
                          label={t('dm.responseRequired.chip')} sx={{ mt: 0.5 }} />
                  ) : null}
                  {/* SB-190 §8 步 5（第二百四十七批）：钟点条紧贴徽标下方。服务端只在
                      「带标记且已发出」的行上给 responseDeadline——排着队的信只有徽标
                      没有钟（时钟自发出起）。里面没有任何正文形状的东西（裁定 3 守区）。 */}
                  <ResponseDeadlineBar deadline={row.responseDeadline} dense />
                </TableCell>
                <TableCell sx={{ maxWidth: 260 }}>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {anchorText(row)}
                  </Typography>
                  {row.anchorStrong ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {t('dm.anchor.strong')}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  {stateChip(row)}
                  {/* 第一百二十五批：「已回复」与状态徽标**并排**，不替换它——原状态仍然是
                      已读，而"读了没回"正是这一格要答的那句话。 */}
                  <RepliedChip row={row} lang={lang} t={t} />
                  <WillSendLine row={row} lang={lang} t={t} />
                  <FailReasonLine row={row} t={t} />
                  <ReportedLine row={row} lang={lang} t={t} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {fmtTime(rowTime(row), lang)}
                  </Typography>
                  <ExposureLine row={row} lang={lang} t={t} variant="caption" />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                    {/* 排队中的信只可能出现在**发件箱**（收件箱恒过滤未发出的），
                        而发送方读自己的信不标已读、不发回执——所以这里不按状态禁用：
                        撤回之前想再看一眼自己写了什么，是最该有的那个动作。 */}
                    <Button size="small" disabled={busy} onClick={() => onOpenRow(row)}>
                      {t('dm.read.open')}
                    </Button>
                    {row.mayWithdraw ? (
                      <Button size="small" color="warning" disabled={busy}
                              onClick={() => setWithdrawRow(row)}>
                        {t('dm.withdraw.action')}
                      </Button>
                    ) : null}
                    {/* 举报窄通道。`mayReport` 只认服务端（= 我是收件人 且 已读 且
                        尚未交出），前端不重算——重算的结局是"按钮亮着、点下去 409"。
                        它**永不出现在发件箱行上**，因为服务端在那一侧就把这一位压成了
                        false：发送方连"这封信被交出去了"都不该知道。 */}
                    {row.mayReport ? (
                      <Button size="small" color="error" disabled={busy}
                              onClick={() => setReportRow(row)}>
                        {t('dm.report.action')}
                      </Button>
                    ) : null}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </DataTable>
      )}
      {/* MESSAGE LIST END */}

      {/* ── 加载更多（SB-159）──────────────────────────────────────────
          **只在服务端说还有更多时才出现**：一个永远在那儿的按钮等于没说话，
          而它不在时才代表"这就是全部"。取回失败时按钮留在原地并出声——
          "取不到"绝不能长得像"没有了"。 */}
      {hasMore ? (
        <Stack sx={{ mt: 2, alignItems: 'center' }}>
          <Button size="small" onClick={loadMore} disabled={moreState.loading}>
            {moreState.loading ? t('dm.loadMoreBusy') : t('dm.loadMore')}
          </Button>
          {moreState.err ? (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              {t('dm.loadMoreError')}
            </Typography>
          ) : null}
        </Stack>
      ) : null}

      {/* ── 「打开就算已读」──────────────────────────────────────────────
          这一问只在收件人打开一封未读信时出现，因为只有那一下会改变世界：
          对方收到回执，而这封信从此再也撤不回。 */}
      <Dialog open={Boolean(confirmRow)} onClose={() => setConfirmRow(null)}>
        <DialogTitle>{t('dm.read.confirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('dm.read.confirmBody')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRow(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" disabled={busy} onClick={() => doOpen(confirmRow)}>
            {t('dm.read.confirmOk')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── 详情：正文唯一的出口 ──────────────────────────────────────── */}
      <Dialog open={Boolean(detail)} onClose={() => { setDetail(null); setDetailErr(null); }}
              fullWidth maxWidth="sm">
        {detail ? (
          <>
            <DialogTitle sx={{ pb: 0.5 }}>
              {t(detail.row.direction === 'out' ? 'dm.dir.out' : 'dm.dir.in',
                 { who: counterpartLabel(detail.row) })}
            </DialogTitle>
            <DialogContent>
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 1 }}
              >
                {/* SB-148 B：详情头也带系统信标记（正文正是在这里被读的）。 */}
                {detail.row.system ? (
                  <Chip size="small" color="info" variant="outlined"
                        label={t('dm.system.chip')} />
                ) : null}
                {/* SB-190 §3.3：要求响应——读正文的那一刻就要看见"这封要答"。 */}
                {detail.row.responseRequired ? (
                  <Chip size="small" color="warning" variant="outlined"
                        label={t('dm.responseRequired.chip')} />
                ) : null}
                {/* SB-190 §8 步 5：详情头同样摆钟点条——正文正是在这里被读的，而「几时前要答」
                    是读完之后立刻要问的下一句。 */}
                <ResponseDeadlineBar deadline={detail.row.responseDeadline} />
                {stateChip(detail.row)}
                {/* 第一百二十五批：详情头同样并排一枚——正文正是在这里被读的，
                    而"我回过它没有"是读完之后立刻要问的下一句（回复钮就在框的底边）。 */}
                <RepliedChip row={detail.row} lang={lang} t={t} />
                <Typography variant="caption" color="text.secondary">
                  {anchorText(detail.row)}
                </Typography>
              </Stack>
              <ExposureLine row={detail.row} lang={lang} t={t} variant="body2" alert />
              {detail.row.readAtMs && detail.row.direction === 'out' ? (
                <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', mb: 1 }}>
                  {t('dm.read.readAt', { t: fmtTime(detail.row.readAtMs, lang) })}
                </Typography>
              ) : null}
              <FailReasonLine row={detail.row} t={t} />
              <ReportedLine row={detail.row} lang={lang} t={t} />
              <Divider sx={{ my: 1.5 }} />
              {detailErr ? (
                <Alert severity="warning">{detailErr}</Alert>
              ) : (
                <MarkdownBody body={detail.body} format={detail.bodyFormat} />
              )}
              {/* SB-150 引用原文：懒取、逐层、恒零副作用（取体见 QuoteBlock 头注）。
                  key 按信 id 键控——切到另一封信时展开态整棵重置，绝不串台。 */}
              {detail.row.anchorKind === 'reply' && detail.row.anchorRef ? (
                <QuoteBlock key={`quote:${detail.row.id}`} msgId={detail.row.anchorRef} />
              ) : null}
            </DialogContent>
            <DialogActions>
              {/* SB-150 回复：显隐问 mayQuickReply（收件方向且未撤回）**且正文真的
                  取回来了**——404/网络错的详情面没有正文，对一封没读到的信亮回复钮，
                  写完只会换来服务端拒锚（410 撤回面由快照 state 修正兜住，这里再把
                  其余失败面一并盖掉，方向保守）。 */}
              {!detailErr && mayQuickReply(detail.row) ? (
                <Button disabled={busy} onClick={() => setReplyRow(detail.row)}>
                  {t('dm.reply.action')}
                </Button>
              ) : null}
              {detail.row.mayWithdraw ? (
                <Button color="warning" disabled={busy}
                        onClick={() => setWithdrawRow(detail.row)}>
                  {t('dm.withdraw.action')}
                </Button>
              ) : null}
              {/* 详情面上也要有：读完这一封才是"要不要交出去"这个问题真正被问的时刻，
                  而收件人第一次点开拿回的 state 就是 read（mayReport 随之为真）。
                  只在列表上给它，等于要求他关掉正文、回到清单里再找一遍那一行。 */}
              {detail.row.mayReport ? (
                <Button color="error" disabled={busy}
                        onClick={() => setReportRow(detail.row)}>
                  {t('dm.report.action')}
                </Button>
              ) : null}
              <Button onClick={() => { setDetail(null); setDetailErr(null); }}>
                {t('common.close')}
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      {/* ── 撤回：要说清楚撤回之后会发生什么 ────────────────────────────
          **按状态分岔**（withdrawDescKey）：一封从未发出的信撤回之后是真的无痕；
          一封已发出的信不是——收件箱里那一行原地留着（撤回只清正文），他看得见
          「来自 张三 · 关于 … · 已撤回 · 14:03」。给两种后果同一句话，就是承诺一个
          做不到的撤回，而那比没有撤回更坏。 */}
      <Dialog open={Boolean(withdrawRow)} onClose={() => setWithdrawRow(null)}>
        <DialogTitle>
          {t('dm.withdraw.title')}
          {/* SB-151 控件级 ❓：撤回边界（对方打开前有效、已发出的会留"已撤回"记录）
              整段讲在手册站内信章——点它导航走，撤回没做、无副作用。 */}
          <HelpLink section="messages" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{t(withdrawDescKey(withdrawRow))}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawRow(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" color="warning" disabled={busy} onClick={doWithdraw}>
            {t('dm.withdraw.action')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── 举报：**不可撤销**这一句必须在点之前说完 ────────────────────────
          这不是礼貌用语，是这条通道不退化成"可反复试探的开关"的全部依据：能收回的
          交出等于一个可以先试探、再收回的探针。所以这一问说三件事——交出去的是**这一
          封的正文**（不是这对人的其它信）、**永久**（收不回来）、**对方不会被告知**
          （举报的目的是让第三方介入，而通知会让被举报方在介入之前先反应）。
          第三句尤其要在**点之前**说：一个以为对方会被通知的人，会因为怕撕破脸而不点；
          一个以为对方不会知道的人，如果实际上会被通知，那是把他推进了他没同意的处境。 */}
      <Dialog open={Boolean(reportRow)} onClose={() => setReportRow(null)}>
        <DialogTitle>{t('dm.report.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('dm.report.confirmBody')}</DialogContentText>
          <Alert severity="warning" sx={{ mt: 1.5 }}>{t('dm.report.irreversible')}</Alert>
          <DialogContentText sx={{ mt: 1.5 }}>{t('dm.report.noNotice')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportRow(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" color="error" disabled={busy} onClick={doReport}>
            {t('dm.report.confirmOk')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 两只实例分工（证伪评审 S2）：blank **恒挂**——「取消后草稿还在」是既成
          行为，一次 key 重挂就会把最多两万字的草稿无声清零，reply 形态不许殃及它；
          reply 按 replyRow 挂、按信 id 键控——预填是**挂载初值**（useState 惰性
          初始化），换一封被回复的信必须整只重挂，否则上一封的预填黏在表单里。 */}
      <ComposeDialog
        key="blank"
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSent={(msg) => { setComposeOpen(false); notify(msg, 'success'); list.reload(); }}
        notifyError={(m) => notify(m, 'error')}
      />
      {replyRow ? (
        <ComposeDialog
          key={`reply:${replyRow.id}`}
          open
          onClose={() => setReplyRow(null)}
          onSent={(msg) => { setReplyRow(null); notify(msg, 'success'); list.reload(); }}
          notifyError={(m) => notify(m, 'error')}
          initialForm={prefillForm}
          lockRecipientAnchor
          lockedAnchorLabel={lockedAnchorLabel}
        />
      ) : null}

      <AppSnackbar snack={snack} onClose={clearSnack} />
    </Box>
  );
}

// ── 「什么时候发出」——机器可读的那一位，文案不许猜 ──────────────────────────
function WillSendLine({ row, lang, t }) {
  const w = willSendText(row);
  if (!w) return null;
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {w.ms == null ? t(w.key) : t(w.key, { t: fmtTime(w.ms, lang) })}
    </Typography>
  );
}

// 终局失败的原因走**枚举串 → i18n**，绝不显示 last_error 的英文原文
//（那是内部细节，且进不了 i18n）。未知的新枚举值 ⇒ 不渲染，而不是印一个原始串
// 给成员看：这一行是给他"下一步做什么"的建议，说不出建议就别说话。
function FailReasonLine({ row, t }) {
  const key = failReasonKey(row?.failReason);
  if (!key) return null;
  return (
    <Typography variant="caption" color="error" sx={{ display: 'block' }}>
      {t(key)}
    </Typography>
  );
}

// ── 第一百二十五批：「已回复」那枚徽标 ──────────────────────────────────────────────
// 判断在 messages.mjs 的 repliedText（有时刻才说话）。**收发两侧都显示**，与 state
// 徽标**并排**而不替换它：一封信可以既"已读"又"已回复"，也可以"已读"而没人回——把
// 两件事压进一个徽标，需求方要的那一格（读了没回）当场消失。
// 时刻进 title 而不是另起一行：列表那一格已经有三条 caption 了，而"什么时候回的"是
// 想知道时才去看的第二层信息。
function RepliedChip({ row, lang, t }) {
  const r = repliedText(row);
  if (!r) return null;
  return (
    <Chip size="small" color="success" variant="outlined" sx={{ ml: 0.5 }}
          title={t(r.key, { t: fmtTime(r.ms, lang) })}
          label={t('dm.replied.chip')} />
  );
}

// ── 举报窄通道：已经交出去的那一行 ──────────────────────────────────────────
// 判断在 messages.mjs 的 reportedText（收件人 + 有时刻才说话）。**恒显示、不会消失**：
// 不可撤销的动作必须在界面上留一条不会消失的痕迹，否则"我到底交出去没有"在下一次
// 会话里就没有答案，而他会再点一次然后拿到 409。
function ReportedLine({ row, lang, t }) {
  const r = reportedText(row);
  if (!r) return null;
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {t(r.key, { t: fmtTime(r.ms, lang) })}
    </Typography>
  );
}

// ── 曝光留痕（契约 B.7）：**收发双方都看得见**，两侧字段名与取值完全一致 ──────
// 发送方那一句必须写明"他看得见这一点"，否则曝光就成了一件他不知情的事。
function ExposureLine({ row, lang, t, variant, alert }) {
  const e = exposureText(row);
  if (!e) return null;
  const text = t(e.key, { t: fmtTime(e.ms, lang) });
  if (alert) return <Alert severity="info" sx={{ mb: 1 }}>{text}</Alert>;
  return (
    <Typography variant={variant} color="text.secondary"
                sx={{ display: 'block', overflowWrap: 'anywhere' }}>
      {text}
    </Typography>
  );
}

// ── 写信 ────────────────────────────────────────────────────────────────────
// 收件人走**下拉+字母过滤**（SB-169，需求方 2026-08-22：原先的裸邮箱手输框要求
// "你已经知道他的邮箱"）。候选来自 /api/me/message-recipients（同组 ∪ 曾给我写过
// 信者——两半各对应一族真能成立的锚点）；**freeSolo 恒开**：端点挂了、或要写一个
// 不在名单里的人（例如刚建的账号），照旧可以手输邮箱——下拉是助力不是笼子
// （同 SubmitTicketPage 的既有先例：options 取不到就退成纯手输）。
// 选中项提交的是**账号 id**（后端 toAccount 兼收 id 与邮箱），所以"系统按名字猜错
// 人"这条路根本不存在；而行内显示的区分符是**成员键不是邮箱**（OQ-C32 (b)）。
// 锚点候选在收件人敲定之后才取——它同时带回 blockedByUnread / pendingMessageId，
// 好让"这封只能排队"或"你已经有一封在排"在他打完 2000 字**之前**就说出来。
// SB-150 预填形态（initialForm + lockRecipientAnchor）：收件人与锚点作为**挂载初值**
// 进来并锁死（disabled），body 恒空由人来写；发送 payload 仍由 composePayload 一处
// 定形。调用侧用 key 保证换目标必重挂——这里没有任何"props 变了就同步表单"的 effect。
function ComposeDialog({ open, onClose, onSent, notifyError,
                         initialForm = null, lockRecipientAnchor = false,
                         lockedAnchorLabel = '' }) {
  const t = useT();
  const { lang } = useLang();
  const [form, setForm] = useState(initialForm ?? EMPTY_FORM);
  // 已敲定的收件人（失焦/回车才变）。预填即敲定：anchors feed 立刻照拉，
  // 既有 pending/blocked 预警对快捷回复**原样生效**（契约 §1A）。
  const [toResolved, setToResolved] = useState(
    initialForm ? String(initialForm.to ?? '').trim() : '');
  // 收件人框里**显示**的那串字。与 form.to（提交值）分开：选中候选之后提交值是
  // 账号 id，而给人看的是「显示名 · 成员键」——把两者合成一个状态，就必然在
  // "显示 id" 与 "提交标签" 之间二选一，两个都是错的。
  const [recipientInput, setRecipientInput] = useState(
    initialForm ? String(initialForm.to ?? '') : '');
  const [docQ, setDocQ] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // 收件人候选（SB-169）。**只取一次**（无 deps）：名单不随输入变化——过滤在本地。
  // 端点失败即空数组，Autocomplete 的 freeSolo 让手输原样可用（房规：降级成手输，
  // 不是降级成不能用）。锁定回复形态不必取——那一格恒 disabled。
  const recipientLoad = useLoad(
    () => (lockRecipientAnchor ? Promise.resolve(null) : api.messageRecipients()), []);
  const recipients = recipientLoad.data ? normalizeRecipients(recipientLoad.data) : [];

  // 锚点候选：**q 不再进请求**（SB-169 片②）——后端的 q 匹配内部 docKey 而下拉显示
  // 本地化标题，照着看到的字打永远匹配不上；且每敲一键重发整个请求。改为一次取足
  // （docLimit 取上限）+ 本地 filterAnchors。
  const feedLoad = useLoad(
    () => (toResolved ? api.messageAnchors(toResolved, { docLimit: 100 })
                      : Promise.resolve(null)),
    [toResolved],
  );
  const feed = feedLoad.data ? normalizeAnchorFeed(feedLoad.data) : null;
  const anchors = feed?.anchors ?? [];
  // 选中的锚点由 pickAnchorId 派生：换了收件人之后旧选择通常不在新候选里，
  // 派生值不可能滞后（同 reports.mjs 的 pickReportKey）。**绝不自动选第一项**。
  // 锁定形态**不过 pickAnchorId**：候选清单的 reply 支只给最近 5 封，被回复的那封
  // 不在其中时锁定值照样成立——它的合法性由服务端 dm_anchor_check 裁，不由候选裁。
  const anchorSel = lockRecipientAnchor
    ? String(form.anchorId ?? '')
    : pickAnchorId(anchors, form.anchorId);

  // 「收件人已停用」排在其余校验**之前**：给一个已离职的人写信的人，正是最需要在
  // 动笔之前被告知"这个人已经不在了"的那一个。锚点端点早就把 toAccountActive 送回来
  // 了，此前一处都没读它——他打完两千字点发送，只拿到服务端 403 回落出的一句
  // "发送失败。"，而那句话没有任何下一步。
  const problem = recipientProblem(feed)
    ?? composeProblem({ ...form, anchorId: anchorSel }, Date.now());

  const onSend = async () => {
    if (busy || problem) return;
    setBusy(true);
    try {
      const r = await api.sendMessage(composePayload({ ...form, anchorId: anchorSel }));
      setForm(EMPTY_FORM);
      setToResolved('');
      setDocQ('');
      onSent(t(r?.state === 'sent_unread' ? 'dm.new.sentNow' : 'dm.new.sent'));
    } catch (e) {
      notifyError(t(errKey(e, { byCode: DM_ERROR_KEYS, fallback: 'dm.new.error' })));
    } finally {
      setBusy(false);
    }
  };

  const anchorOptionLabel = (a) => {
    const key = anchorLabelKey(a.kind);
    // reply 的 label 恒空（清单面一样不许出现正文），只给时刻。
    const ref = a.kind === 'reply'
      ? (a.labelMs ? fmtTime(a.labelMs, lang) : a.ref)
      : (a.label || a.ref);
    const base = key ? t(key, { ref }) : `${a.kind} ${a.ref}`;
    return a.strong ? `${base} · ${t('dm.anchor.strong')}` : base;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('dm.new.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* 收件人：下拉 + 头部字母过滤（Autocomplete 自带），freeSolo 保住手输。
              inputValue 与 form.to 同步——手输时 form.to 就是那串字；选中候选时
              form.to 变成**账号 id**（提交值），而输入框显示的是那一行的标签。
              过滤用 MUI 默认（对 getOptionLabel 的结果做不分大小写的包含匹配）：
              标签里既有显示名也有成员键，两者都搜得到；自己再写一份就是第二处
              会漂的过滤规则。 */}
          <Autocomplete
            freeSolo
            size="small"
            options={recipients}
            getOptionLabel={(o) => (typeof o === 'string' ? o : recipientOptionLabel(o, recipients))}
            isOptionEqualToValue={(o, v) => o?.id === v?.id}
            groupBy={(o) => t(o.sameGroup ? 'dm.new.toGroupSame' : 'dm.new.toGroupHistory')}
            inputValue={recipientInput}
            onInputChange={(_e, v, reason) => {
              // reset＝选中之后 MUI 回写标签，别把它当成手输（否则 form.to 会被
              // 标签串覆盖，而标签不是任何一种合法的 toAccount）。
              if (reason === 'reset') return;
              setRecipientInput(v ?? '');
              set('to', (v ?? '').trim());
            }}
            onChange={(_e, v) => {
              if (!v) { set('to', ''); setToResolved(''); setRecipientInput(''); return; }
              if (typeof v === 'string') {         // freeSolo 回车：他自己打的那串
                set('to', v.trim());
                setToResolved(v.trim());
                return;
              }
              set('to', v.id);                     // ← 提交 id，不是任何名字
              setToResolved(v.id);
              setRecipientInput(recipientOptionLabel(v, recipients));
            }}
            onBlur={() => { if (form.to) setToResolved(String(form.to).trim()); }}
            disabled={busy || lockRecipientAnchor}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('dm.new.to')}
                helperText={t(lockRecipientAnchor ? 'dm.reply.lockedHelper' : 'dm.new.toHelper')}
                onKeyDown={(e) => { if (e.key === 'Enter' && form.to) setToResolved(String(form.to).trim()); }}
              />
            )}
          />

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t('dm.new.anchor')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t('dm.new.anchorHelper')}
            </Typography>
            {/* 锁定形态：锚点恒为 reply:<被回复信 id>，不给候选、不给搜索——一个
                必被服务端拒绝的改动没有资格在表单上看起来可改。feed 照拉，但只为
                下面的 pending/blocked 预警；它的空清单/加载失败都不该在这里说话
                （"你们没有共同引用"对一封已存在的来信是句假话）。 */}
            {lockRecipientAnchor ? (
              <TextField size="small" fullWidth value={lockedAnchorLabel} disabled />
            ) : !toResolved ? null : feedLoad.status === 'loading' ? (
              <Typography variant="body2" color="text.secondary">
                {t('dm.new.anchorLoading')}
              </Typography>
            ) : feedLoad.status === 'error' ? (
              <Alert severity="error">
                {t(errKey(feedLoad.error, { byCode: DM_ERROR_KEYS, fallback: 'dm.new.error' }))}
              </Alert>
            ) : anchors.length === 0 ? (
              <Alert severity="warning">{t('dm.new.anchorEmpty')}</Alert>
            ) : (
              <Stack spacing={1}>
                {/* 搜索框与下拉合一（SB-169 片②）：头部输入即过滤，过滤在**本地**
                    （filterAnchors 匹配 label 与 ref），不再每敲一键重发请求。
                    freeSolo 关闭——锚点必须是候选里的一项（手打一个不存在的锚点，
                    服务端必拒），这与收件人那格恰好相反。 */}
                <Autocomplete
                  size="small"
                  options={filterAnchors(anchors, docQ)}
                  disabled={busy}
                  value={anchors.find((a) => a.id === anchorSel) ?? null}
                  onChange={(_e, v) => set('anchorId', v ? v.id : '')}
                  inputValue={docQ}
                  onInputChange={(_e, v, reason) => {
                    if (reason !== 'reset') setDocQ(v ?? '');
                  }}
                  getOptionLabel={(a) => anchorOptionLabel(a)}
                  isOptionEqualToValue={(o, v) => o?.id === v?.id}
                  filterOptions={(opts) => opts}
                  renderInput={(params) => (
                    <TextField {...params} label={t('dm.new.anchorSearch')} />
                  )}
                />
                {feed?.anchorsTruncated ? (
                  <Typography variant="caption" color="text.secondary">
                    {t('dm.new.anchorTruncated')}
                  </Typography>
                ) : null}
              </Stack>
            )}
          </Box>

          {feed?.pendingMessageId ? (
            <Alert severity="warning">
              {t('dm.new.pending', { id: feed.pendingMessageId })}
            </Alert>
          ) : feed?.blockedByUnread ? (
            <Alert severity="info">
              {t('dm.new.blocked')}
              <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                {t('dm.new.blockedWindow')}
              </Box>
            </Alert>
          ) : null}

          <TextField
            label={t('dm.new.body')}
            multiline
            minRows={5}
            value={form.body}
            disabled={busy}
            onChange={(e) => set('body', e.target.value)}
            helperText={`${form.body.length} / ${DM_BODY_MAX}`}
            error={form.body.length > DM_BODY_MAX}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="dm-format-label">{t('dm.new.bodyFormat')}</InputLabel>
              <Select
                labelId="dm-format-label"
                label={t('dm.new.bodyFormat')}
                value={form.bodyFormat}
                disabled={busy}
                onChange={(e) => set('bodyFormat', e.target.value)}
              >
                <MenuItem value="markdown">Markdown</MenuItem>
                <MenuItem value="plain">Plain</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="datetime-local"
              label={t('dm.new.schedule')}
              helperText={t('dm.new.scheduleHelper')}
              value={form.schedule}
              disabled={busy}
              onChange={(e) => set('schedule', e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ flexGrow: 1 }}
            />
          </Stack>

          {/* SB-190 §3.3：「要求响应」——发出时即定、事后不可补（列只在 create() 的 INSERT
              写，没有任何路由能事后置位），所以这一位只能在编写器上、只能在按发送之前。 */}
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={form.responseRequired === true}
                  disabled={busy}
                  onChange={(e) => set('responseRequired', e.target.checked)}
                />
              }
              label={t('dm.new.responseRequired')}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('dm.new.responseRequiredHelper')}
            </Typography>
          </Box>

          {problem ? (
            <Typography variant="body2" color="text.secondary">
              {t(problem, { n: form.body.length })}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={onSend} disabled={busy || Boolean(problem)}>
          {t('dm.new.send')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── SB-150 引用原文：折叠、懒取、逐层递归 ────────────────────────────────────
// 三条硬约束（需求方 2026-08-18 裁①「展开恒零副作用」）：
//  · 取体**只走 api.peekMessage**——展开一层绝不落已读：「读」保持为对那封信本身的
//    郑重动作，套娃深层引到"发给我而未读"的信也只 peek。本文件的引用路径里出现任何
//    有副作用的取体调用，messages.test.mjs 的结构判据当场红。
//  · **永不自动拉链**：每层都等人点一次展开（初值 closed，组件里没有 useEffect）。
//  · 错误按码分岔：410 = 原文已被发送方撤回（撤回=正文物理清空，这是诚实的答案，
//    不是故障，不给重试）；404 = 原文不可见（与"不存在"同形，隐匿闸原样透传，
//    同样不给重试）；其余（网络/5xx）可重试。
// 展开态只活在本组件的 state 里，而调用侧按信 id 传 key：切换信件整棵卸载重建，
// 上一封的展开态不可能串到下一封。
function QuoteBlock({ msgId }) {
  const t = useT();
  const { lang } = useLang();
  // phase: closed | loading | open | gone | invisible | error
  const [st, setSt] = useState({ phase: 'closed' });

  const load = async () => {
    setSt({ phase: 'loading' });
    try {
      const r = await api.peekMessage(msgId);
      const row = normalizeMessage(r);
      // normalizeMessage 对畸形响应返回 null（服务端今天恒给 id，但代理截断/上游
      // 漂移不归它管）；open 态下面要对 row 取属性，null 不设防就是整页白屏。
      if (!row) { setSt({ phase: 'error' }); return; }
      setSt({ phase: 'open', row,
              body: typeof r.body === 'string' ? r.body : '',
              bodyFormat: r.bodyFormat });
    } catch (e) {
      if (e?.status === 410) setSt({ phase: 'gone' });
      else if (e?.status === 404) setSt({ phase: 'invisible' });
      else setSt({ phase: 'error' });
    }
  };

  if (st.phase === 'closed') {
    return (
      <Button size="small" onClick={load} sx={{ mt: 1 }}>
        {t('dm.quote.expand')}
      </Button>
    );
  }
  if (st.phase === 'loading') {
    return <CircularProgress size={18} sx={{ mt: 1, display: 'block' }} />;
  }
  if (st.phase === 'gone' || st.phase === 'invisible') {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {t(st.phase === 'gone' ? 'dm.quote.withdrawn' : 'dm.quote.notFound')}
      </Typography>
    );
  }
  if (st.phase === 'error') {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}
             action={<Button size="small" onClick={load}>{t('common.retry')}</Button>}>
        {t('dm.quote.error')}
      </Alert>
    );
  }
  // open：该层 = 锚点行 + 正文；原文自身也是 reply 时给**下一层**的展开钮（懒取）。
  return (
    <Box sx={{ mt: 1, pl: 1.5, borderLeft: 2, borderColor: 'divider' }}>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ alignItems: 'center', flexWrap: 'wrap' }}
      >
        <Typography variant="caption" color="text.secondary">
          {anchorLineText(st.row, lang, t)}
        </Typography>
        <Button size="small" onClick={() => setSt({ phase: 'closed' })}>
          {t('dm.quote.collapse')}
        </Button>
      </Stack>
      <MarkdownBody body={st.body} format={st.bodyFormat} />
      {st.row.anchorKind === 'reply' && st.row.anchorRef ? (
        <QuoteBlock key={`q:${st.row.anchorRef}`} msgId={st.row.anchorRef} />
      ) : null}
    </Box>
  );
}
